import {
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  model,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { DslError, tokenize } from '@core/filters/path-filter/dsl/lexer';
import {
  SearchBarState,
  SyntaxRole,
  SyntaxSpan,
  parseSearchBar,
  printSearchBar
} from '@core/filters/path-filter/dsl/search-bar';
import { vocabularyShorthand } from '@core/filters/path-filter/vocabulary/default-operators';
import { FilterVocabulary } from '@core/filters/path-filter/vocabulary/vocabulary';
import { CurrentLangService } from '@core/services/common/current-lang.service';
import { ICONS } from '@core/services/icons/icons.service';

import { DropdownComponent } from '@shared/components/dropdown/dropdown.component';
import { AvailableLanguages } from '@shared/types/translation.type';
import { isEditableTarget } from '@shared/utils/editable-target.utils';

import {
  FIELD_TYPE_ICONS,
  PATH_FILTER_I18N,
  associationLabel,
  fieldLabel,
  sortableLabel
} from '../path-filter-labels';
import { SortOption } from '../sort/sort-options';
import {
  SearchSuggestion,
  SuggestionLabels,
  SuggestionList,
  SuggestionSection,
  searchSuggestions
} from './search-suggestions';
import { sortQualifierIssue } from './sort-check';

/**
 * Start and end of each `sort:…` qualifier in `text`, in the order the parser collected them.
 * `syntax` tells a sort qualifier apart from one read as free text, which is a search word.
 */
function sortQualifierSpans(
  text: string,
  syntax: SyntaxSpan[]
): { start: number; end: number }[] {
  const tokens = tokenize(text, { searchBar: true });
  const isFree = (at: number): boolean =>
    syntax.some(({ start, end, role }) => role === 'free' && start <= at && at < end);
  const spans: { start: number; end: number }[] = [];
  tokens.forEach((token, i) => {
    if (
      token.kind !== 'word' ||
      token.value !== 'sort' ||
      tokens[i + 1]?.kind !== ':' ||
      isFree(token.start)
    )
      return;
    // A qualifier is written without spaces, so it runs over every token touching the previous one.
    let last = i + 1;
    while (tokens[last + 1]?.start === tokens[last].end) last++;
    spans.push({ start: token.start, end: tokens[last].end });
  });
  return spans;
}

/** Literal class names, so Tailwind finds them. Free text keeps the input's own colour. */
const ROLE_CLASS: Record<SyntaxRole, string> = {
  field: 'text-(--syntax-field)',
  operator: 'text-(--syntax-operator)',
  string: 'text-(--syntax-string)',
  number: 'text-(--syntax-number)',
  uid: 'text-(--syntax-uid)',
  edge: 'text-(--syntax-edge)',
  free: 'text-text-primary',
  error: 'text-(--syntax-error)'
};

const ERROR_UNDERLINE =
  'decoration-danger underline decoration-wavy underline-offset-[3px]';

interface Segment {
  text: string;
  role: SyntaxRole | null;
  bad: boolean;
}

@Component({
  selector: 'app-path-filter-search-bar',
  imports: [TranslatePipe, DropdownComponent],
  templateUrl: './path-filter-search-bar.component.html',
  styles: `
    :host {
      display: block;
      min-width: 0;
    }
  `
})
export class PathFilterSearchBarComponent {
  private static instances = 0;

  vocabulary = input.required<FilterVocabulary>();
  /** Written only with a state that parsed: half-typed text never reaches the host. */
  state = model.required<SearchBarState>();
  /** `/` outside a text field focuses this bar; one bar per page owns it, or they would race. */
  shortcut = input(false);
  /** Sorts offered after `sort:` beyond the vocabulary's, such as the grid's KPI columns. */
  extraSortOptions = input<SortOption[]>([]);

  protected readonly ICONS = ICONS;
  private readonly translate = inject(TranslateService);
  private readonly currentLang = inject(CurrentLangService).currentLang;
  private readonly id = `path-filter-search-${++PathFilterSearchBarComponent.instances}`;
  protected readonly errorId = `${this.id}-error`;
  protected readonly listId = `${this.id}-suggestions`;
  protected readonly text = signal('');
  protected readonly error = signal<DslError | null>(null);
  protected readonly focused = signal(false);
  private readonly caret = signal(0);
  private readonly dismissed = signal(false);
  private readonly inputRef = viewChild.required<ElementRef<HTMLInputElement>>('input');
  private readonly resolver = computed(() => vocabularyShorthand(this.vocabulary()));
  /** JSON of the state this bar last wrote; its echo from the host must not reformat the text. */
  private lastWritten: string | null = null;

  /**
   * The text cut at every span and error boundary, so each piece carries one colour and the
   * underline can cover an error range that crosses several spans. An error past the end of the
   * text gets blank padding to underline.
   */
  protected readonly segments = computed<Segment[]>(() => {
    const text = this.text();
    const spans = parseSearchBar(text, this.resolver()).spans;
    const error = this.error();
    const bad = error
      ? {
          start: Math.min(error.start, text.length),
          end: Math.max(error.end, Math.min(error.start, text.length) + 1)
        }
      : null;
    const cuts = new Set([0, text.length]);
    for (const { start, end } of spans) {
      cuts.add(start);
      cuts.add(end);
    }
    if (bad) {
      cuts.add(bad.start);
      cuts.add(Math.min(bad.end, text.length));
    }
    const points = [...cuts].sort((a, b) => a - b);
    const segments: Segment[] = [];
    for (let i = 0; i + 1 < points.length; i++) {
      const [start, end] = [points[i], points[i + 1]];
      segments.push({
        text: text.slice(start, end),
        role: spans.find((span) => span.start <= start && end <= span.end)?.role ?? null,
        bad: !!bad && start >= bad.start && end <= bad.end
      });
    }
    if (bad && bad.end > text.length) {
      segments.push({
        text: ' '.repeat(bad.end - Math.max(bad.start, text.length)),
        role: null,
        bad: true
      });
    }
    return segments;
  });

  protected segmentClass({ role, bad }: Segment): string {
    const colour = role ? ROLE_CLASS[role] : 'text-text-primary';
    return bad ? `${colour} ${ERROR_UNDERLINE}` : colour;
  }

  private readonly labels = computed<SuggestionLabels>(() => {
    const lang = this.currentLang() as AvailableLanguages;
    const vocabulary = this.vocabulary();
    return {
      field: (field) => fieldLabel(field, lang, this.translate),
      association: (association) => associationLabel(association, lang, this.translate),
      sortable: (sortable) => sortableLabel(sortable, vocabulary, lang, this.translate),
      aggregate: (aggregate) =>
        this.translate.instant(`${PATH_FILTER_I18N}.sort.aggregate.${aggregate}`),
      relativeDate: (dsl) =>
        this.translate.instant(`${PATH_FILTER_I18N}.relative_dates.${dsl}`),
      operatorExample: (op) =>
        this.translate.instant(`${PATH_FILTER_I18N}.search.operator_examples.${op}`),
      quantifier: (quantifier) =>
        this.translate.instant(`${PATH_FILTER_I18N}.search.quantifier_${quantifier}`)
    };
  });

  protected readonly list = computed<SuggestionList | null>(() =>
    this.focused() && !this.dismissed()
      ? searchSuggestions(
          this.text(),
          this.caret(),
          this.vocabulary(),
          this.labels(),
          this.extraSortOptions()
        )
      : null
  );
  protected readonly suggestions = computed<SearchSuggestion[]>(
    () => this.list()?.items ?? []
  );
  protected readonly hint = computed(() => this.list()?.hint ?? null);
  protected readonly isOpen = computed(
    () => this.suggestions().length > 0 || this.hint() !== null
  );
  /** Section eyebrows only help when the list mixes kinds. */
  protected readonly showsSections = computed(
    () => new Set(this.suggestions().map((suggestion) => suggestion.section)).size > 1
  );
  /**
   * Index into the suggestions; -1 for none. Narrowed by typing, the first row is preselected; opened
   * on an empty boundary nothing is, so Enter there inserts nothing the user did not choose.
   */
  protected readonly highlighted = linkedSignal<SuggestionList | null, number>({
    source: this.list,
    computation: (list) => (list?.narrowed && list.items.length > 0 ? 0 : -1)
  });
  protected readonly activeOptionId = computed(() => {
    const index = this.highlighted();
    return index >= 0 && index < this.suggestions().length ? this.optionId(index) : null;
  });
  protected readonly closeSuggestions = () => this.dismissed.set(true);

  constructor() {
    effect(() => {
      const id = this.activeOptionId();
      if (id) document.getElementById(id)?.scrollIntoView({ block: 'nearest' });
    });

    effect(() => {
      const state = this.state();
      const resolver = this.resolver();
      untracked(() => {
        if (JSON.stringify(state) === this.lastWritten) return;
        this.lastWritten = null;
        this.text.set(printSearchBar(state, resolver));
        this.error.set(null);
      });
    });
  }

  protected optionId(index: number): string {
    return `${this.listId}-${index}`;
  }

  protected iconOf(suggestion: SearchSuggestion): string | null {
    if (suggestion.type) return FIELD_TYPE_ICONS[suggestion.type];
    switch (suggestion.section) {
      case 'edges':
        return ICONS.treeStructure;
      case 'sort':
        return ICONS.sortAscending;
      default:
        return null;
    }
  }

  protected startsSection(index: number): SuggestionSection | null {
    if (!this.showsSections()) return null;
    const suggestions = this.suggestions();
    const section = suggestions[index].section;
    return index === 0 || suggestions[index - 1].section !== section ? section : null;
  }

  /** Focus, a click or a caret move reopens a list closed with Escape, at the new caret. */
  protected reopen(input: HTMLInputElement): void {
    this.caret.set(input.selectionStart ?? input.value.length);
    this.dismissed.set(false);
  }

  protected trackCaret(input: HTMLInputElement): void {
    this.caret.set(input.selectionStart ?? input.value.length);
  }

  protected onInput(value: string, caret: number | null): void {
    this.text.set(value);
    this.caret.set(caret ?? value.length);
    this.dismissed.set(false);
    const result = parseSearchBar(value, this.resolver());
    if (!result.ok) {
      this.error.set(result.error);
      return;
    }
    // The last applied state stays while a term is still being typed.
    if (result.pending) {
      this.error.set(null);
      return;
    }
    const sortError = this.sortError(value, result.value, result.spans);
    if (sortError) {
      this.error.set(sortError);
      return;
    }
    this.error.set(null);
    this.lastWritten = JSON.stringify(result.value);
    this.state.set(result.value);
  }

  protected onKeydown(event: KeyboardEvent): void {
    const suggestions = this.suggestions();
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        if (event.key === 'ArrowDown' && this.dismissed()) {
          event.preventDefault();
          this.dismissed.set(false);
          if (this.suggestions().length > 0) {
            this.highlighted();
            this.highlighted.set(0);
          }
          return;
        }
        const count = suggestions.length;
        if (count === 0) return;
        event.preventDefault();
        this.highlighted.update((index) =>
          event.key === 'ArrowDown'
            ? (index + 1) % count
            : index <= 0
              ? count - 1
              : index - 1
        );
        return;
      }
      case 'Enter':
      case 'Tab': {
        const suggestion = suggestions[this.highlighted()];
        if (!suggestion) return;
        event.preventDefault();
        this.apply(suggestion);
        return;
      }
      case 'Escape':
        // Hosts such as a modal close on a document-level Escape; this one is spent on the list.
        if (this.isOpen()) event.stopPropagation();
        this.closeSuggestions();
        return;
    }
  }

  /** The first sort qualifier the data layer would refuse, as an error spanning its text. */
  private sortError(
    text: string,
    state: SearchBarState,
    syntax: SyntaxSpan[]
  ): DslError | null {
    const spans = sortQualifierSpans(text, syntax);
    for (const [i, sort] of state.sort.entries()) {
      const expected = sortQualifierIssue(sort, this.vocabulary());
      if (expected === null) continue;
      const { start, end } = spans[i] ?? { start: 0, end: text.length };
      return new DslError(start, end, expected, text.slice(start, end));
    }
    return null;
  }

  /** The underline overlay cannot scroll by itself; it follows the input's horizontal scroll. */
  protected syncScroll(input: HTMLInputElement, overlay: HTMLElement): void {
    overlay.scrollLeft = input.scrollLeft;
  }

  protected apply(suggestion: SearchSuggestion): void {
    const text = this.text();
    const next =
      text.slice(0, suggestion.from) + suggestion.insert + text.slice(suggestion.to);
    const caret = suggestion.from + suggestion.insert.length;
    const element = this.inputRef().nativeElement;
    element.value = next;
    element.focus();
    element.setSelectionRange(caret, caret);
    this.onInput(next, caret);
  }

  protected clear(): void {
    const element = this.inputRef().nativeElement;
    element.value = '';
    element.focus();
    this.onInput('', 0);
  }

  @HostListener('document:keydown', ['$event'])
  protected onShortcut(event: KeyboardEvent): void {
    if (!this.shortcut() || event.key !== '/' || isEditableTarget(event.target)) return;
    event.preventDefault();
    this.inputRef().nativeElement.focus();
  }
}
